import React from 'react';

/**
 * Shared building blocks for the Hire Confidence section.
 *
 * The section's job is to make a weighted score legible: every figure is shown
 * as what was earned against what was available, so the total is arithmetic a
 * reviewer can check rather than a number to trust.
 */

/* ─── Palette ───────────────────────────────────────────────── */

/**
 * One colour per component, used consistently across its row icon, border and
 * expanded panel — colour is what ties a breakdown back to the part of the
 * score it explains.
 *
 * Drawn from the app's own palette in `_variables.less` (`--theme-*`) rather
 * than fresh hex, so a re-themed deployment carries through. Fallbacks match
 * the defaults declared there.
 *
 * `text` shades are hand-picked rather than themed: the base warning and
 * success colours are too light to read as small text, and the `-darker`
 * custom properties have no fallback declared.
 */
const THEME_COLORS = {
    warning: 'var(--theme-warning, #FFB946)',
    primary: 'var(--theme-primary, #5F3DB1)',
    info: 'var(--theme-info, #4B80E7)',
    success: 'var(--theme-success, #5AE399)',
};

const COMPONENT_THEMES = {
    roleMatch: {
        hex: THEME_COLORS.warning,
        tint: 'rgba(255, 185, 70, 0.14)',
        icon: 'fa-bullseye',
        soft: 'bg-[#FFFBF2]',
        softHover: 'hover:bg-[#FFFBF2]',
        border: 'border-[color:var(--theme-warning,#FFB946)]',
        borderHover: 'group-hover:border-[color:var(--theme-warning,#FFB946)]',
        text: 'text-[#8A5A00]',
    },
    engagementFit: {
        hex: THEME_COLORS.primary,
        tint: 'rgba(95, 61, 177, 0.12)',
        icon: 'fa-file-signature',
        soft: 'bg-[#FAF8FF]',
        softHover: 'hover:bg-[#FAF8FF]',
        border: 'border-primary',
        borderHover: 'group-hover:border-primary',
        text: 'text-primary',
    },
    marketDemand: {
        hex: THEME_COLORS.info,
        tint: 'rgba(75, 128, 231, 0.12)',
        icon: 'fa-bolt',
        soft: 'bg-[#F3F7FE]',
        softHover: 'hover:bg-[#F3F7FE]',
        border: 'border-[color:var(--theme-info,#4B80E7)]',
        borderHover: 'group-hover:border-[color:var(--theme-info,#4B80E7)]',
        text: 'text-[#2A55A8]',
    },
    priorOutcomes: {
        hex: THEME_COLORS.success,
        tint: 'rgba(90, 227, 153, 0.16)',
        icon: 'fa-trophy',
        soft: 'bg-[#F1FCF6]',
        softHover: 'hover:bg-[#F1FCF6]',
        border: 'border-[color:var(--theme-success,#5AE399)]',
        borderHover: 'group-hover:border-[color:var(--theme-success,#5AE399)]',
        text: 'text-[#0F7A46]',
    },
};

/**
 * The four-point diamond used to mark AI-derived figures across the app —
 * the same glyph the AI Resume Score callout carries in the Match section.
 */
const AiDiamondIcon = ({ className }) => (
    <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        className={className || 'h-3.5 w-3.5 shrink-0 text-primary'}
    >
        <path d="M12 0c.9 6.6 4.4 10.1 11 11-6.6.9-10.1 4.4-11 11-.9-6.6-4.4-10.1-11-11C7.6 10.1 11.1 6.6 12 0Z" />
    </svg>
);

const themeFor = (key) => COMPONENT_THEMES[key] || COMPONENT_THEMES.roleMatch;

/**
 * What each component measures, in one line. The scores mean nothing without
 * it — a 50 on Market Demand is the platform average, not a failing grade.
 *
 * Shared by the profile's accordion rows and the Role Match modal on the
 * candidate tile, which otherwise frame the score differently: the profile row
 * sits under a running total and so carries its weight and earned points, while
 * the modal has no total on screen for those to be a share of.
 */
const COMPONENT_NOTES = {
    roleMatch: 'Profile match and AI resume read, evenly blended',
    engagementFit: 'AI review of their proposal against this role',
    marketDemand: 'How sought-after they are across the platform',
    priorOutcomes: 'Results delivered on past Wripple engagements',
};

/* ─── Numbers ───────────────────────────────────────────────── */

/**
 * A 0-100 score as a filled track. Reads at a glance next to its siblings,
 * which a bare number does not.
 *
 * `isInactive` is for criteria the role never asked for. Those score 100 by
 * definition, and a full amber bar reads as an achievement — grey says "not
 * assessed" while still showing the figure that went into the maths.
 */
const ScoreBar = ({ label, score, detail, colorClass = 'bg-primary', isInactive }) => {
    const hasScore = score !== null && score !== undefined;

    return (
        <div>
            <div className="mb-1 flex flex-row items-baseline justify-between gap-3">
                <p className="mb-0 text-sm font-medium text-[#1F2029]">{label}</p>
                <p className="mb-0 whitespace-nowrap text-xs text-[#8B8F9A]">
                    {detail}
                    {detail && hasScore ? ' · ' : ''}
                    {hasScore && (
                        <span className={TB.mapClass('font-bold tabular-nums', {
                            'text-[#1F2029]': !isInactive,
                            'text-[#A2A6AF]': isInactive,
                        })}>
                            {Math.round(score)}
                        </span>
                    )}
                </p>
            </div>

            <div className="h-2 w-full overflow-hidden rounded-full bg-[#EFEFF3]">
                <div
                    className={TB.mapClass('h-full rounded-full transition-[width] duration-700 ease-out', {
                        'bg-[#D8DAE0]': isInactive,
                        [colorClass]: !isInactive,
                    })}
                    style={{ width: `${hasScore ? Math.min(Math.max(score, 0), 100) : 0}%` }}
                />
            </div>
        </div>
    );
};

/* ─── States ────────────────────────────────────────────────── */

/**
 * Why a component scored nothing. Always says which — an unexplained blank
 * invites the reader to assume the pipeline is broken.
 */
const EmptyPanel = ({ icon = 'fa-hourglass-half', title, children }) => (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-[#CFD1DA] bg-[#FAFAFB] px-4 py-8 text-center">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#A2A6AF] shadow-[inset_0_0_0_1px_#E2E3EA]">
            <i className={`fas ${icon}`} />
        </span>

        <p className="mb-0 text-sm font-bold text-[#1F2029]">{title}</p>

        <p className="mb-0 max-w-[420px] text-xs leading-[1.5] text-[#595D68]">
            {children}
        </p>
    </div>
);

export {
    COMPONENT_THEMES,
    COMPONENT_NOTES,
    THEME_COLORS,
    themeFor,
    AiDiamondIcon,
    ScoreBar,
    EmptyPanel,
};
